import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notification } from "antd";
import ActionBar from "~/layout/ActionBar";
import { Button, Loading, Modal, Icon } from "~/components";
import { useCan } from "~/hooks";
import {
	saleCallSourceActions,
	saleCallSourceErrorSelector,
	saleCallSourceFilterSelector,
	saleCallSourceItemsSelector,
	saleCallSourceLoadingSelector,
	saleCallSourcePaginationSelector,
	saleCallSourceStatsSelector,
} from "../saleCallSourceSlice";
import { apiError, handleRequest } from "~/utils";
import { saleApi } from "~/api";
import {
	CallSourceSearchBar,
	SaleCallSourceContactTable,
	SaleCallSourceContactForm,
	SaleCallSourceUploadForm,
	SaleCallSourceTransferForm,
} from "../components";

function SaleCallSource() {

	const dispatch   = useDispatch();
	const items      = useSelector(saleCallSourceItemsSelector);
	const loading    = useSelector(saleCallSourceLoadingSelector);
	const error      = useSelector(saleCallSourceErrorSelector);
	const pagination = useSelector(saleCallSourcePaginationSelector);
	const filter     = useSelector(saleCallSourceFilterSelector);
	const stats      = useSelector(saleCallSourceStatsSelector);

	const canListAll    = useCan('salesCallSourceListAll');
	const canListGroup  = useCan('salesCallSourceListGroup');
	const canListLeader = useCan('salesCallSourceListLeader');
	const canTransfer   = canListAll || canListGroup || canListLeader;

	const [selectedRowKeys, setSelectedRowKeys] = useState([]);

	const [openModal, setOpenModal] = useState({
		add      : false,
		import   : false,
		transfer : false,
	});

	const handleModalOpen  = (modal) => setOpenModal((prev) => ({ ...prev, [modal]: true }));
	const handleModalClose = (modal) => setOpenModal((prev) => ({ ...prev, [modal]: false }));

	useEffect(() => {
		dispatch(saleCallSourceActions.fetchData({ ...filter }));
	}, [filter]);

	const reload = () => dispatch(saleCallSourceActions.fetchData({ ...filter }));

	if (error) {
		notification.error({ message: 'Lỗi', description: error });
	}

	const handleFilterChange = (newFilter) => {
		dispatch(saleCallSourceActions.setFilter(newFilter));
	};

	const handleSearchChange = (newFilter) => {
		dispatch(saleCallSourceActions.setFilterWithDebounce(newFilter));
	};

	const handlePaginationChange = (page) => {
		dispatch(saleCallSourceActions.setFilter({ ...filter, page }));
	};

	const handlePatch = (id, patch) => {
		dispatch(saleCallSourceActions.patchContact({ id, patch }));
	};

	// Thêm 1 liên hệ
	const handleAddContact = async (payload) => {
		let [err, response] = await handleRequest(saleApi.callSourceContactCreate(payload));
		let message = apiError('Thêm liên hệ thất bại', err, response);
		if (!message) {
			notification.success({ message: 'Thành công', description: `Đã thêm ${payload.name}` });
			handleModalClose('add');
			reload();
		}
	};

	// Nhập danh sách từ Excel (tạo nguồn mới)
	const handleImport = async ({ contacts }) => {
		let [err, response] = await handleRequest(saleApi.callSourceAdd({ contacts }));
		let message = apiError('Nhập liên hệ thất bại', err, response);
		if (!message) {
			const added   = response.totalAdded   || 0;
			const skipped = response.skippedCount || 0;
			if (skipped > 0) {
				notification.warning({
					message: 'Hoàn thành',
					description: `Đã thêm ${added} liên hệ. Bỏ qua ${skipped} số trùng.`,
					duration: 6,
				});
			} else {
				notification.success({ message: 'Thành công', description: `Đã thêm ${added} liên hệ` });
			}
			handleModalClose('import');
			reload();
		}
	};

	// Chuyển nguồn cho nhân viên khác
	const handleTransfer = async ({ toUserId, fromUserId, mode }) => {
		const payload = { toUserId, fromUserId, mode };
		if (mode === 'selected') payload.ids = selectedRowKeys;
		let [err, response] = await handleRequest(saleApi.callSourceTransfer(payload));
		let message = apiError('Chuyển nguồn thất bại', err, response);
		if (!message) {
			notification.success({ message: 'Thành công', description: response?.message || `Đã chuyển ${response?.count || ''} nguồn` });
			setSelectedRowKeys([]);
			handleModalClose('transfer');
			reload();
		}
	};

	const rowSelection = canTransfer ? {
		selectedRowKeys,
		onChange: setSelectedRowKeys,
		preserveSelectedRowKeys: true,
	} : undefined;

	return (
		<Fragment>
			<ActionBar title={'Nguồn gọi điện'}>
				<Button outline leftIcon={Icon.plusCircle} onClick={() => handleModalOpen('add')}>
					Thêm liên hệ
				</Button>
				<Button outline leftIcon={Icon.upload} onClick={() => handleModalOpen('import')}>
					Nhập Excel
				</Button>
				{canTransfer && (
					<Button outline leftIcon={Icon.switch} onClick={() => handleModalOpen('transfer')}>
						Chuyển nguồn{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
					</Button>
				)}
				<Button outline onClick={reload}>{Icon.reload}</Button>
			</ActionBar>

			<div className="container">
				<div className="content">
					{/* Bộ lọc + thống kê */}
					<CallSourceSearchBar filter={filter} stats={stats} onChange={handleFilterChange} onSearchChange={handleSearchChange} />

					{loading && <Loading />}

					<SaleCallSourceContactTable
						contacts={items || []}
						pagination={pagination}
						loading={loading}
						onPaginationChange={handlePaginationChange}
						onReload={reload}
						onPatch={handlePatch}
						rowSelection={rowSelection}
						showOwner={canTransfer}
					/>
				</div>

				{/* Modal: Thêm liên hệ */}
				<Modal
					title="Thêm liên hệ"
					visible={openModal.add}
					onCancel={() => handleModalClose('add')}
					size={"lg"}
				>
					<div className="pd-2">
						{/* key đổi theo trạng thái mở để form được reset mỗi lần mở lại */}
						<SaleCallSourceContactForm
							key={openModal.add ? 'open' : 'closed'}
							onHandleSubmit={handleAddContact}
							onCancel={() => handleModalClose('add')}
						/>
					</div>
				</Modal>

				{/* Modal: Nhập Excel */}
				<Modal
					title="Nhập liên hệ từ Excel"
					visible={openModal.import}
					onCancel={() => handleModalClose('import')}
					size={"xl"}
				>
					<SaleCallSourceUploadForm
						key={openModal.import ? 'open' : 'closed'}
						onHandleSubmit={handleImport}
					/>
				</Modal>

				{/* Modal: Chuyển nguồn */}
				<Modal
					title="Chuyển nguồn cho nhân viên khác"
					visible={openModal.transfer}
					onCancel={() => handleModalClose('transfer')}
					size={"lg"}
				>
					<div className="pd-2">
						<SaleCallSourceTransferForm
							key={openModal.transfer ? 'open' : 'closed'}
							selectedCount={selectedRowKeys.length}
							onSubmit={handleTransfer}
						/>
					</div>
				</Modal>
			</div>
		</Fragment>
	);
}

export default SaleCallSource;
